import { IonButton, IonText } from '@ionic/react';
import { ComponentProps, KeyboardEvent, useCallback } from 'react';

import styles from '@components/DropdownButton.module.css';
import Icon from '@components/text/Icon';
import useOpener from '@hooks/useOpener';

type Props = ComponentProps<typeof IonButton> & {
  options: { name: string; value: string }[];
  setValue: (value: string) => void;
  value: string;
};

const DropdownButton = ({ options, setValue, value, ...buttonProps }: Props) => {
  const dropdownOpener = useOpener();

  const handleValueSelect = useCallback(
    (value: string) => {
      dropdownOpener.close();
      setValue(value);
    },
    [dropdownOpener, setValue]
  );

  const handleDropdownKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'ArrowDown') {
        dropdownOpener.open();
      } else if (dropdownOpener.isOpen && event.code === 'ArrowUp') {
        dropdownOpener.close();
      }
    },
    [dropdownOpener]
  );

  return (
    <>
      <div className={styles.buttonAndDropdown}>
        <IonButton
          className={dropdownOpener.isOpen ? styles.buttonOpen : styles.button}
          data-test="dropdown-button"
          fill="solid"
          shape="round"
          size="default"
          {...buttonProps}
        />
        <div
          className={dropdownOpener.isOpen ? styles.selectOtherOpen : styles.selectOther}
          onClick={dropdownOpener.toggle}
          onKeyUp={handleDropdownKeyUp}
          tabIndex={0}
        >
          <Icon ariaLabelKey="accessibility.ariaLabels.toggle" icon="caretDown" />
        </div>
      </div>
      <div className={dropdownOpener.isOpen ? undefined : 'ion-hide'}>
        <div className={styles.valueSelect}>
          <div className={styles.dropdown}>
            {options
              .filter(option => option.value !== value)
              .map(option => (
                <div
                  className={styles.item}
                  key={option.value}
                  onClick={() => handleValueSelect(option.value)}
                  onKeyUp={event => {
                    if (event.code === 'Enter') {
                      handleValueSelect(option.value);
                    }
                  }}
                  tabIndex={0}
                >
                  <IonText>{option.name}</IonText>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DropdownButton;
